<template>
  <a-button type="primary" @click="showNewProjectModal">
    <PlusOutlined />&nbsp;新增專案
  </a-button>
  <a-modal v-model:open="isOpen" width="800px" title="專案新增" :centered="isModalCentered" 
      :maskClosable="isMaskClosable" :closable="isMaskClosable" :cancelButtonProps="cancelButtonProps" 
      :confirm-loading="isSavingProject" okText="儲存" cancelText="關閉" @ok="saveNewProject">
    <a-divider />
    <a-form ref="newProjectForm" :model="formState" name="basic" :rules="formRules" :label-col="{ span: 5 }" 
        :wrapper-col="{ span: 13 }" :disabled="isSavingProject">
      <a-form-item ref="projectName" label="專案名稱" name="projectName">
        <a-input v-model:value="formState.projectName" />
      </a-form-item>
      <a-form-item ref="companyName" label="客戶(公司)名稱" name="companyName">
        <a-input v-model:value="formState.companyName" />
      </a-form-item>
      <a-form-item label="專案設定" name="projectConfig">
        <a-checkbox-group v-model:value="formState.projectConfig">
          <a-space direction="vertical">
            <a-checkbox value="requireBasic" name="projectConfig" :disabled="formState.projectConfig.includes('onlyImported') || formState.projectConfig.includes('onlyImportedJobNo')">基本資料必填&nbsp;<span style="color: red;">(給未提供使用者身分證字號的客戶使用)</span></a-checkbox>
            <a-checkbox value="onlyImported" name="projectConfig" :disabled="formState.projectConfig.includes('requireBasic') || formState.projectConfig.includes('disableIdVerify') || formState.projectConfig.includes('onlyImportedJobNo')">僅允許已匯入人員以證件編號登入&nbsp;<span style="color: red;">(需提供使用者身分證字號)</span></a-checkbox>
            <a-checkbox value="onlyImportedJobNo" name="projectConfig" :disabled="formState.projectConfig.includes('requireBasic') || formState.projectConfig.includes('disableIdVerify') || formState.projectConfig.includes('onlyImported')">僅允許已匯入人員以工號或學號登入&nbsp;<span style="color: red;">(需提供使用者身分證字號)</span></a-checkbox>
            <a-checkbox value="disableIdVerify" name="projectConfig" :disabled="formState.projectConfig.includes('onlyImported') || formState.projectConfig.includes('onlyImportedJobNo')">停用證件編號驗證</a-checkbox>
          </a-space>
        </a-checkbox-group>
      </a-form-item>
      <a-form-item v-if="canSelectQuestionnaireForm" label="問卷填寫介面" name="questionnaireForm">
        <a-radio-group v-model:value="formState.questionnaireForm">
          <a-space direction="horizontal" wrap>
            <a-radio value="LEGACY">傳統版問卷</a-radio>
            <a-radio value="MODERN">新版問卷</a-radio>
          </a-space>
        </a-radio-group>
      </a-form-item>
      <a-form-item label="健檢日期(起訖)" name="checkDateRange">
        <a-range-picker v-model:value="formState.checkDateRange" style="width: 80%" :placeholder="dateRangePlaceholder" />
      </a-form-item>
      <a-form-item label="問卷填寫日期(起訖)" name="expireDateRange">
        <a-range-picker v-model:value="formState.expireDateRange" style="width: 80%" :placeholder="dateRangePlaceholder" />
      </a-form-item>
      <a-form-item label="問卷類型" name="surveyType">
        <a-checkbox-group v-model:value="formState.surveyType">
          <a-space direction="vertical">
            <a-checkbox value="OVER_WORK" v-if="authorizedQuestTypes.includes('OVER_WORK')" name="surveyType" :disabled="formState.surveyType.includes('OVER_WORK_MIND')">過勞評估</a-checkbox>
            <a-checkbox value="OVER_WORK_MIND" v-if="authorizedQuestTypes.includes('OVER_WORK_MIND')" name="surveyType" :disabled="formState.surveyType.includes('OVER_WORK')">過勞與心理評估</a-checkbox>
            <a-checkbox value="BSRS5" v-if="authorizedQuestTypes.includes('BSRS5')" name="surveyType">心情溫度計(簡式健康量表)</a-checkbox>
            <a-checkbox value="MUSCUL" v-if="authorizedQuestTypes.includes('MUSCUL')" name="surveyType">肌肉骨骼自覺症狀評量表</a-checkbox>
            <a-checkbox value="OVER_LOAD" v-if="authorizedQuestTypes.includes('OVER_LOAD')" name="surveyType">過負荷問卷</a-checkbox>
            <a-checkbox value="GENERAL_PHYSICAL" v-if="authorizedQuestTypes.includes('GENERAL_PHYSICAL')" name="surveyType">勞工一般體檢</a-checkbox>
            <a-checkbox value="ELDERLY_WORK_ADAPTABILITY" v-if="authorizedQuestTypes.includes('ELDERLY_WORK_ADAPTABILITY')" name="surveyType">中高齡工作適能評估&nbsp;<span style="color: red;">(僅開放給未匯入與45歲以上人員填寫)</span></a-checkbox>
            <a-checkbox value="WORKPLACE_UNLAWFUL_ACTS" v-if="authorizedQuestTypes.includes('WORKPLACE_UNLAWFUL_ACTS')" name="surveyType">職場不法侵害風險評估</a-checkbox>
            <a-checkbox value="SELF_APPRAISAL_NUTRITION" v-if="authorizedQuestTypes.includes('SELF_APPRAISAL_NUTRITION')" name="surveyType">自我評核-營養</a-checkbox>
          </a-space>
        </a-checkbox-group>
      </a-form-item>
      <a-form-item label="備註" name="remark">
        <a-textarea v-model:value="formState.remark" />
      </a-form-item>
    </a-form>
    <a-divider />
  </a-modal>
</template>
<script setup>
import { PlusOutlined } from "@ant-design/icons-vue";
import { ref, reactive, defineEmits } from 'vue';

import common from "@/common";
import config from "@/config";

const newProjectForm = ref();
const authorizedQuestTypes = ref([]);
const canSelectQuestionnaireForm = ref(false);
const dateRangePlaceholder = ["開始日期", "結束日期"];
const isOpen = ref(false);
const isModalCentered = ref(true);
const isMaskClosable = ref(false);
const isSavingProject = ref(false);
const cancelButtonProps = ref({
  disabled: isSavingProject
});
const formState = reactive({
  projectName: '',
  companyName: '',
  projectConfig: [],
  questionnaireForm: 'LEGACY',
  checkDateRange: [],
  expireDateRange: [],
  surveyType: [],
  remark: ''
});

const emit = defineEmits(['cancel', 'submit-success']);

function newDateWithoutTime(date) {
  return new Date(date.year(), date.month(), date.date(), 0, 0, 0, 0);
}

const formRules = {
  projectName: [{
    required: true,
    message: '請輸入專案名稱',
    trigger: ['change', 'blur']
  }], 
  companyName: [{
    required: true,
    message: '請輸入客戶(公司)名稱',
    trigger: ['change', 'blur']
  }],
  checkDateRange: [{
    required: true,
    message: '請選取健檢日期(起訖)',
    trigger: ['change']
  }],
  expireDateRange: [{
    required: true,
    message: '請選取問卷填寫日期(起訖)',
    trigger: ['change']
  }, {
    message: '「問卷填寫起始日」不可晚於「健檢起始日」',
    trigger: ['change'],
    validator: (rule, value) => {
      if (value == null || 
          value.length <= 1 || 
          formState.checkDateRange == null || 
          formState.checkDateRange.length == 0 || 
          newDateWithoutTime(value[0]) <= newDateWithoutTime(formState.checkDateRange[0])) {
        return Promise.resolve();
      }
      return Promise.reject(rule.message);
    }
  }],
  surveyType: [{
    type: 'array',
    required: true,
    message: '請至少選取一個問卷類型',
    trigger: ['change']
  }]
};

const resetModalState = (userConfig) => {
  if (newProjectForm.value != null) {
    newProjectForm.value.resetFields();
  }
  isSavingProject.value = false;
  formState.projectName = '';
  formState.companyName = '';
  formState.projectConfig = [...(userConfig?.projectConfigs || [])];
  formState.questionnaireForm = canSelectQuestionnaireForm.value ? 'MODERN' : 'LEGACY';
  formState.checkDateRange = [];
  formState.expireDateRange = [];
  formState.surveyType = [];
  if (userConfig != null && userConfig.surveyTypes != null) { 
    userConfig.surveyTypes.forEach(surveyType => {
      if (authorizedQuestTypes.value.includes(surveyType)) {
        formState.surveyType.push(surveyType);
      }
    });
  }
  formState.remark = '';
}

const initNewProjectNodal = (userConfig) => {
  resetModalState(userConfig);
  setTimeout(() => isOpen.value = true, 100);
}

const showNewProjectModal = () => {
  common.getRequest(config.mgmtUserEntity, (response) => {
    authorizedQuestTypes.value = response.data.authorizedQuestTypes;
    canSelectQuestionnaireForm.value = response.data.userConfig?.allowQuestionnaireFormSelection === true;
    initNewProjectNodal(response.data.userConfig);
  }, (error) => {
    console.error("Failed to get Mgmt User entity. " + error);
    authorizedQuestTypes.value = [];
    canSelectQuestionnaireForm.value = false;
    initNewProjectNodal();
  });
  
};

const saveNewProject = () => {
  newProjectForm.value.validate()
  .then(() => {
    isSavingProject.value = true;
    common.postRequest(config.mgmtSaveProjectEntity, formState, 
      (response) => {
        isSavingProject.value = false;
        isOpen.value = false;
        emit('submit-success', response.data);
      }, () => {
        isSavingProject.value = false;
      });
  }).catch(() => { });
};

</script>
<style scoped>
button,
span {
  display: inline-flex;
  align-items: center;
}
</style>
